import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { ProductCard } from "@/components/site/ProductCard";
import { staggerContainer } from "@/lib/motion";
import {
  DDGS_INTRO,
  DDGS_HERO,
  MAIZE_DERIVATIVES,
  RICE_DERIVATIVES,
  OILSEEDS_DERIVATIVES,
} from "@/data/products";

const SubHeader = ({ n, title, kicker, blurb }) => (
  <Reveal className="mb-10 lg:mb-14">
    <div className="flex items-baseline gap-6">
      <span className="font-display text-5xl md:text-6xl leading-none text-[#7A4E24]/40">
        {n}
      </span>
      <div>
        <h3 className="font-display text-3xl md:text-5xl leading-[0.95] tracking-tight text-[#3D2817]">
          {title}
        </h3>
        {kicker && (
          <p className="mt-2 text-xs md:text-sm uppercase tracking-[0.22em] text-[#7A4E24]">
            {kicker}
          </p>
        )}
      </div>
    </div>
    {blurb && (
      <p className="mt-5 max-w-3xl text-base md:text-lg leading-relaxed text-[#6B4423] pl-0 md:pl-20">
        {blurb}
      </p>
    )}
  </Reveal>
);

const Grid = ({ children, cols = 4 }) => {
  const colsCls =
    cols === 3
      ? "sm:grid-cols-2 lg:grid-cols-3"
      : cols === 2
      ? "sm:grid-cols-2"
      : "sm:grid-cols-2 lg:grid-cols-4";
  return (
    <motion.div
      className={`grid grid-cols-1 gap-x-8 gap-y-14 ${colsCls}`}
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.05 }}
    >
      {children}
    </motion.div>
  );
};

export const FeedIngredients = () => (
  <section
    id="feed"
    data-testid="feed-section"
    className="bg-[#F5F2E9] py-24 lg:py-40"
  >
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <Reveal className="mb-16 lg:mb-24 max-w-3xl">
        <p className="mb-5 text-xs md:text-sm uppercase tracking-[0.25em] text-[#7A4E24]">
          &#8212; Animal Feed Ingredients
        </p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.9] tracking-tight text-[#3D2817]">
          The building blocks of
          <span className="italic text-[#7A4E24]"> world-class animal nutrition.</span>
        </h2>
      </Reveal>

      {/* 01 — DDGS */}
      <SubHeader
        n="01"
        title="DDGS"
        kicker="Dried Distiller's Grains with Solubles"
        blurb={DDGS_INTRO}
      />
      <Grid cols={2}>
        {DDGS_HERO.map((p, i) => (
          <ProductCard
            key={p.id}
            testId={`product-${p.id}`}
            name={p.name}
            subtitle={p.subtitle}
            image={p.image}
            description={p.description}
            size="lg"
            shape={i === 0 ? "arch" : "rect"}
          />
        ))}
      </Grid>

      {/* 02 — Maize & Its Derivatives */}
      <div className="mt-24 lg:mt-32">
        <SubHeader
          n="02"
          title="Maize & Its Derivatives"
          blurb={"From the whole grain to precision-processed derivatives \u2014 every downstream application of golden maize, backed by consistent quality."}
        />
        <Grid cols={4}>
          {MAIZE_DERIVATIVES.map((p) => (
            <ProductCard
              key={p.id}
              testId={`product-${p.id}`}
              name={p.name}
              subtitle={p.subtitle}
              image={p.image}
              description={p.description}
              size="sm"
            />
          ))}
        </Grid>
      </div>

      {/* 03 — Rice Derivatives */}
      <div className="mt-24 lg:mt-32">
        <SubHeader
          n="03"
          title="Rice Derivatives"
          blurb={"Nutrient-dense rice bran and its refined oils \u2014 sourced with precision and quality assurance."}
        />
        <Grid cols={3}>
          {RICE_DERIVATIVES.map((p) => (
            <ProductCard
              key={p.id}
              testId={`product-${p.id}`}
              name={p.name}
              subtitle={p.subtitle}
              image={p.image}
              description={p.description}
              size="md"
            />
          ))}
        </Grid>
      </div>

      {/* 04 — Oil Seeds Derivatives */}
      <div className="mt-24 lg:mt-32">
        <SubHeader
          n="04"
          title="Oil Seeds Derivatives"
          blurb={"Plant-based protein cornerstones for advanced feed formulations \u2014 from mustard to rapeseed."}
        />
        <Grid cols={4}>
          {OILSEEDS_DERIVATIVES.map((p) => (
            <ProductCard
              key={p.id}
              testId={`product-${p.id}`}
              name={p.name}
              subtitle={p.subtitle}
              image={p.image}
              description={p.description}
              size="sm"
            />
          ))}
        </Grid>
      </div>
    </div>
  </section>
);
